"use client";

import Image from "next/image";
import Link from "@/components/ui/Link";
import { useRouter } from "next/navigation";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string;
  customClass?: string;
};

type Props = {
  category?: FieldConfig;
  sectionUrl?: string;
  title?: FieldConfig;
  excerpt?: FieldConfig;
  timeAgo?: FieldConfig;
  imageSrc?: FieldConfig;
  slug?: string;
  /** Mark as LCP image - preloads for faster rendering */
  priority?: boolean;
  imageMetadata?: ImageMetadata;
  templatetype?: FieldConfig;
};

export default function HorizontalSmallRightImageCategoryNews({
  category = { hidden: false, value: "BUSINESS", customClass: "" },
  sectionUrl,
  title = { hidden: false, value: "மெட்ரோவில் மெட்ரோ சாரங்கம் தோன்றும் மணி; ஆகஸ்டில் தொடக்கம்", customClass: "" },
  excerpt = { hidden: false, value: "சென்னை மெட்ரோவில் மெட்ரோ சாரங்கம் தோன்றும் மணி ஆகஸ்டு மாதம் தொடங்கப்படும் என மெட்ரோ ரயில் நிர்வாகம் தெரிவித்தது", customClass: "" },
  timeAgo = { hidden: false, value: "Wed, 17 May 2023", customClass: "" },
  imageSrc = { hidden: false, value: FALLBACK_IMAGE, customClass: "" },
  slug,
  priority = false,
  imageMetadata,
  templatetype = { hidden: false, value: "", customClass: "" },
}: Props) {
  const router = useRouter();

  const categoryValue = category?.value || "";
  const titleValue = title?.value || "";
  const excerptValue = excerpt?.value || "";
  const timeAgoValue = getTimeAgo(timeAgo?.value);
  const imageSrcValue = getImageUrl(imageSrc?.value) || FALLBACK_IMAGE;
  const isLiveBlog = templatetype?.value === "live-blog" || templatetype?.value === "true";

  const handleCategoryClick = (e: React.MouseEvent) => {
    if (sectionUrl) {
      e.preventDefault();
      e.stopPropagation();
      router.push(sectionUrl);
    }
  };

  const content = (
    <article className="p-4">
      <div className="flex flex-row-reverse gap-3">
        {/* RIGHT: IMAGE */}
        {!imageSrc?.hidden && (
          <div className={`relative w-[106px] h-[76px] lg:w-[134px] lg:h-[134px] flex-shrink-0 overflow-hidden ${imageSrc?.customClass || ""}`}>
            <Image
              src={imageSrcValue}
              alt={titleValue}
              width={134}
              height={134}
              priority={priority}
              className="object-cover w-full h-full !h-[76px] lg:!h-[134px]"
              style={getFocusPointStyle(imageMetadata)}
              sizes="(min-width: 1024px) 134px, 106px"
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              placeholder="blur"
              blurDataURL={BLUR_PLACEHOLDER}
            />
          </div>
        )}

        {/* LEFT: CONTENT */}
        <div className="flex flex-col justify-between gap-2 w-full">
          <div className="flex flex-col gap-2">
            {/* Category */}
            {!category?.hidden && (
              <>
                {sectionUrl ? (
                  <span
                    className={`text-11-tamil-600 text-012061 tracking-wide uppercase hover:underline cursor-pointer ${category?.customClass || ""}`}
                    onClick={handleCategoryClick}
                  >
                    {categoryValue}
                  </span>
                ) : (
                  <div className={`text-11-tamil-600 text-012061 tracking-wide uppercase ${category?.customClass || ""}`}>
                    {categoryValue}
                  </div>
                )}
              </>
            )}

            {/* Title */}
            {!title?.hidden && (
 <h3 className={`text-1B1B1B text-14-tamil-700 font-medium leading-[1.4] ${title?.customClass || ""}`}>
                {isLiveBlog && (
                  <span className="inline-flex items-center mr-2 align-middle">
                    <span className="relative inline-flex h-4 w-4 items-center justify-center">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-40 animate-[wave_1.5s_ease-out_infinite]" />
                      <span className="absolute inline-flex h-3 w-3 rounded-full bg-red-500 opacity-60 animate-[wave_1.5s_ease-out_0.3s_infinite]" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                    </span>
                  </span>
                )}
                {titleValue}
              </h3>
            )}

            {/* Excerpt */}
            {!excerpt?.hidden && (
              <p className={`text-4D4D4D text-12-tamil-400 leading-[1.3] line-clamp-2 ${excerpt?.customClass || ""}`}>
                {excerptValue}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            {!timeAgo?.hidden && (
              <span className={`text-12-tamil-500 text-1B1B1B ${timeAgo?.customClass || ""}`}>{timeAgoValue}</span>
            )}
            <MessageIcon
              id="right-image-news-message-icon"
              slug={slug}
              category={BOOKMARK_CATEGORIES.NEWS}
              title={titleValue}
              thumbnailUrl={imageSrcValue}
              description={excerptValue}
              articleUrl={slug ? `/${slug}` : undefined}
            />
          </div>
        </div>
      </div>
    </article>
  );

  if (slug) {
    return (
      <Link href={`/${slug}`} className="block  transition-colors cursor-pointer">
        {content}
      </Link>
    );
  }

  return content;
}
